import { ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const MAX_DECIMAL_PLACES = 12;

type NumericInputProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "className" | "defaultValue" | "max" | "min" | "onChange" | "step" | "type" | "value"
>;

export type NumericStepperProps = NumericInputProps & {
  /** The current value. An empty string remains visually empty until a step is used. */
  value: number | string;
  /** Called with a clamped, precision-safe value after user input or a step. */
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Optional unit or currency displayed between the input and the step controls. */
  suffix?: React.ReactNode;
  /** Classes applied to the outer, positioned wrapper. */
  className?: string;
  /** Classes applied to the native input itself. */
  inputClassName?: string;
};

function decimalPlaces(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) return 0;

  const [, exponentPart] = value.toString().toLowerCase().split("e");
  const exponent = exponentPart ? Number(exponentPart) : 0;
  const coefficient = value.toString().toLowerCase().split("e")[0] ?? "";
  const fractionLength = coefficient.split(".")[1]?.length ?? 0;

  return Math.min(MAX_DECIMAL_PLACES, Math.max(0, fractionLength - exponent));
}

function roundToPrecision(value: number, precision: number) {
  const rounded = Number(value.toFixed(precision));
  return Object.is(rounded, -0) ? 0 : rounded;
}

function formatValue(value: number, precision: number) {
  return String(roundToPrecision(value, precision));
}

function clampValue(value: number, min?: number, max?: number) {
  const finiteMin = min !== undefined && Number.isFinite(min) ? min : undefined;
  const finiteMax = max !== undefined && Number.isFinite(max) ? max : undefined;

  let next = value;
  if (finiteMin !== undefined) next = Math.max(finiteMin, next);
  if (finiteMax !== undefined) next = Math.min(finiteMax, next);
  return next;
}

function normalizeValue(value: number, min: number | undefined, max: number | undefined, precision: number) {
  return roundToPrecision(clampValue(Number.isFinite(value) ? value : 0, min, max), precision);
}

export const NumericStepper = React.forwardRef<HTMLInputElement, NumericStepperProps>(function NumericStepper(
  {
    value,
    onChange,
    min,
    max,
    step = 1,
    suffix,
    className,
    inputClassName,
    id,
    disabled,
    placeholder,
    "aria-label": ariaLabel,
    "aria-invalid": ariaInvalid,
    "aria-describedby": ariaDescribedBy,
    onBlur,
    ...inputProps
  },
  ref,
) {
  const safeStep = Number.isFinite(step) && step > 0 ? step : 1;
  const numericValue = typeof value === "number" ? value : Number(value);
  const precision = Math.min(
    MAX_DECIMAL_PLACES,
    Math.max(decimalPlaces(numericValue), decimalPlaces(min), decimalPlaces(max), decimalPlaces(safeStep)),
  );
  const normalizedValue = normalizeValue(numericValue, min, max, precision);
  const displayValue = typeof value === "string" && value.trim() === "" ? "" : formatValue(normalizedValue, precision);
  const [inputValue, setInputValue] = React.useState(displayValue);

  React.useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  const updateValue = (nextValue: number) => {
    const next = normalizeValue(nextValue, min, max, precision);
    setInputValue(formatValue(next, precision));
    if (next !== numericValue) onChange(next);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    setInputValue(rawValue);

    // Keep incomplete decimal values editable without sending NaN to the parent.
    if (!rawValue.trim() || rawValue === "-" || rawValue === "." || rawValue === "-.") return;

    const parsedValue = Number(rawValue);
    if (Number.isFinite(parsedValue)) onChange(normalizeValue(parsedValue, min, max, precision));
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const parsedValue = Number(inputValue);
    if (inputValue.trim() === "" || !Number.isFinite(parsedValue)) {
      setInputValue(formatValue(normalizedValue, precision));
    } else {
      updateValue(parsedValue);
    }
    onBlur?.(event);
  };

  const increment = (direction: 1 | -1) => {
    updateValue(normalizedValue + direction * safeStep);
  };

  const atMinimum = min !== undefined && Number.isFinite(min) && normalizedValue <= min;
  const atMaximum = max !== undefined && Number.isFinite(max) && normalizedValue >= max;
  const label = ariaLabel || "la valeur";

  return (
    <div className={cn("relative w-full", className)} data-slot="numeric-stepper">
      <input
        {...inputProps}
        ref={ref}
        id={id}
        type="number"
        inputMode="decimal"
        value={inputValue}
        min={Number.isFinite(min) ? min : undefined}
        max={Number.isFinite(max) ? max : undefined}
        step={safeStep}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        data-slot="numeric-stepper-input"
        onChange={handleInputChange}
        onBlur={handleBlur}
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 h-12 w-full min-w-0 rounded-lg border border-input bg-card px-4 py-1 pr-12 text-base tabular-nums shadow-xs transition-[color,box-shadow] outline-none [appearance:textfield] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          suffix ? "pr-20" : undefined,
          inputClassName,
        )}
      />

      {suffix ? (
        <span className="pointer-events-none absolute right-10 top-1/2 max-w-[35%] -translate-y-1/2 truncate text-sm font-medium text-muted-foreground">
          {suffix}
        </span>
      ) : null}

      <span className="absolute inset-y-0 right-0 flex w-9 flex-col overflow-hidden rounded-r-lg border-l border-input/70">
        <button
          type="button"
          aria-label={`Augmenter ${label}`}
          disabled={disabled || atMaximum}
          onClick={() => increment(1)}
          className="grid min-h-0 flex-1 place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:z-10 focus-visible:bg-accent focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-35"
        >
          <ChevronUp aria-hidden="true" className="size-4" strokeWidth={2.25} />
        </button>
        <button
          type="button"
          aria-label={`Diminuer ${label}`}
          disabled={disabled || atMinimum}
          onClick={() => increment(-1)}
          className="grid min-h-0 flex-1 place-items-center border-t border-input/70 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:z-10 focus-visible:bg-accent focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-35"
        >
          <ChevronDown aria-hidden="true" className="size-4" strokeWidth={2.25} />
        </button>
      </span>
    </div>
  );
});

NumericStepper.displayName = "NumericStepper";
